import {useForm} from '@tanstack/react-form';
import {ValidationError} from 'yup';
import React from 'react';
import {Button} from '~/components/Button';
import {InputType} from '~/components/Controls/Input';
import {CheckboxField, FormColumn, FormControls, FormRow, FormTitle, InputField, IntentText} from '~/components/Form';
import formStyle from '~/components/Form/index.module.scss';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';
import {SelectBidderField} from '~/modules/Bidders/components/SelectBidder';
import {BidderFormCtx, Field, setNestedValue} from '~/modules/Bidders/forms/BidderFormBridge';
import {defaultPreTargeting, IPreTargetingConfig, PreTargetingSchema} from '~/modules/Bidders/models/PreTargeting';

export interface IPreTargetingFormActions {
    setSubmitting: (submitting: boolean) => void;
    setStatus: (nextStatus: string | undefined) => void;
}

export interface IPreTargetingForm {
    preTargeting?: IPreTargetingConfig;
    onSubmit?: (values: IPreTargetingConfig, formActions: IPreTargetingFormActions, close: IModalComponentClose) => void | Promise<void>;
}

const FormShell = (props: React.FormHTMLAttributes<HTMLFormElement>) => {
    const classes = [formStyle.form, props.className].filter(Boolean);
    const childrenArray = React.Children.toArray(props.children);
    const titles: React.ReactNode[] = [];
    const content: React.ReactNode[] = [];
    const controls: React.ReactNode[] = [];

    childrenArray.forEach(child => {
        if (!React.isValidElement(child)) {
            content.push(child);
            return;
        }
        if (child.type === FormControls) {
            controls.push(child);
        } else if (child.type === FormTitle) {
            titles.push(child);
        } else {
            content.push(child);
        }
    });

    return <form {...props} className={classes.join(' ')}>
        {titles}
        <div className={formStyle.formBody}>
            {content}
        </div>
        {controls}
    </form>;
};

export const PreTargetingForm: IModalComponent<IPreTargetingForm> = props => {
    const closeModal = props.close ?? (() => undefined);
    const [status, setStatus] = React.useState<string>();
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, any>>({});

    const form = useForm({
        defaultValues: props.preTargeting ?? defaultPreTargeting,
        validators: {
            onSubmit: ({value}) => {
                try {
                    PreTargetingSchema({} as any).validateSync(value, {abortEarly: false});
                    setFieldErrors({});
                    return undefined;
                } catch (error) {
                    if (!(error instanceof ValidationError)) {
                        return 'Validation failed';
                    }
                    const newErrors: Record<string, any> = {};
                    const issues = error.inner.length > 0 ? error.inner : [error];
                    issues.forEach(issue => {
                        if (!issue.path) return;
                        setNestedValue(newErrors, issue.path, issue.message);
                    });
                    setFieldErrors(newErrors);
                    return 'Validation failed';
                }
            }
        },
        onSubmit: async ({value}) => {
            if (!props.onSubmit) return;
            const formActions: IPreTargetingFormActions = {
                setSubmitting: (_submitting: boolean) => undefined,
                setStatus
            };
            await Promise.resolve(props.onSubmit(value, formActions, closeModal));
        }
    });

    return <form.Subscribe selector={state => state}>
        {state => {
            const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                setStatus(undefined);
                form.setFieldValue(fieldName as never, fieldValue as never);
            };

            const formBridge = {
                errors: fieldErrors,
                touched: {},
                submitCount: state.submissionAttempts,
                setFieldValue,
                setStatus,
                setSubmitting: (_submitting: boolean) => undefined,
            };

            const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                event?.preventDefault();
                void form.handleSubmit();
            };

            const preTargeting = state.values;
            const isSubmitting = state.isSubmitting;

            return <BidderFormCtx.Provider value={{form, formBridge}}>
                <FormShell onSubmit={handleSubmit}>
                    <FormTitle>{preTargeting.id ? 'Edit config' : 'Create config'}</FormTitle>
                    {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                    <FormRow>
                        <FormColumn>
                            <Field name='bidderId'
                                label='Bidder'
                                options={{
                                    searchable: true,
                                    placeholder: 'Bidder',
                                    disabled: isSubmitting || Boolean(props.preTargeting?.bidderId)
                                }}
                                component={SelectBidderField}
                            />
                            <Field name='title'
                                label='Title'
                                options={{
                                    placeholder: 'Title',
                                    disabled: isSubmitting
                                }}
                                component={InputField}
                            />
                        </FormColumn>
                    </FormRow>
                    <FormRow>
                        <FormColumn>
                            <Field name='priority'
                                label='Priority'
                                options={{
                                    placeholder: 'Priority',
                                    type: InputType.INTEGER,
                                    disabled: isSubmitting
                                }}
                                component={InputField}
                            />
                            <Field name='allowCloseDelay'
                                label='Allow Close Delay'
                                options={{
                                    placeholder: 'Allow Close Delay',
                                    disabled: isSubmitting,
                                    type: InputType.FLOAT
                                }}
                                component={InputField}
                            />
                        </FormColumn>
                        <FormColumn>
                            <Field name='allowCache'
                                label='Allow Cache'
                                options={{
                                    placeholder: 'allow Cache',
                                    disabled: isSubmitting
                                }}
                                component={CheckboxField}
                            />
                            <Field name='active'
                                label='Active'
                                options={{
                                    placeholder: 'Active',
                                    disabled: isSubmitting
                                }}
                                component={CheckboxField}
                            />
                        </FormColumn>
                    </FormRow>
                    <FormControls>
                        <Button disabled={isSubmitting}
                            onClick={closeModal}
                        >Cancel</Button>
                        <Button type='submit'
                            disabled={isSubmitting}
                            intent={Intent.PRIMARY}
                            rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                        >Save</Button>
                    </FormControls>
                </FormShell>
            </BidderFormCtx.Provider>;
        }}
    </form.Subscribe>;
};
